import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { TranslateService } from '@ngx-translate/core';
import { EcomCashoutMapPoint } from './model';

type PlaceAutocompleteElementConstructor = typeof google.maps.places['PlaceAutocompleteElement'];

declare global {
  interface Window {
    initEcomCashoutMap?: () => void;
  }
}

const MAP_POINTS_URL = '/ecomcashout/map-points';
const GOOGLE_MAPS_API_KEY = 'AIzaSyBbWpFqCf9f5oNqHl9iy_vvb3JWOYfEfso';
const GOOGLE_MAPS_CALLBACK = 'initEcomCashoutMap';
const GOOGLE_MAPS_MAP_ID = 'DEMO_MAP_ID';
const DEFAULT_MAP_POSITION: google.maps.LatLngLiteral = {
  lat: 46.9758283,
  lng: 28.596835,
};
const DEFAULT_MAP_ZOOM = 7;
const MAX_SEARCH_ZOOM = 17;

@Injectable()
export class EcomCashoutSetecomMapService {
  private readonly document = inject(DOCUMENT);
  private readonly translateService = inject(TranslateService);
  private map: google.maps.Map | null = null;
  private markerClusterer: MarkerClusterer | null = null;

  async load(mapElement: HTMLElement): Promise<void> {
    try {
      const points = await this.getMapPoints();
      await this.renderMapFromPoints(mapElement, points);
      mapElement.classList.add('is-google-map');
    } catch {
      mapElement.classList.remove('is-google-map');
    }
  }

  private async getMapPoints(): Promise<EcomCashoutMapPoint[]> {
    const response = await fetch(MAP_POINTS_URL, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('CashOut map points request failed');
    }

    return (await response.json()) as EcomCashoutMapPoint[];
  }

  private async renderMapFromPoints(
    mapElement: HTMLElement,
    points: EcomCashoutMapPoint[],
  ): Promise<void> {
    const networkMap = this.document.createElement('div');
    networkMap.id = 'NetworkMap';
    networkMap.className = 'height-100 radius bordered';
    networkMap.style.minHeight = '21.3rem';

    mapElement.replaceChildren(networkMap);

    await this.ensureGoogleMapsApi();
    await this.renderGoogleMap(networkMap, points);
  }

  private ensureGoogleMapsApi(): Promise<void> {
    if (this.isGoogleMapsLoaded()) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      window.initEcomCashoutMap = () => resolve();

      const language = this.document.documentElement.lang || 'ro-RO';
      const googleMapsUrl =
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}` +
        `&libraries=geometry,marker,places&callback=${GOOGLE_MAPS_CALLBACK}` +
        `&language=${encodeURIComponent(language)}&loading=async`;

      this.appendExternalScript(googleMapsUrl).catch(reject);
    });
  }

  private isGoogleMapsLoaded(): boolean {
    return Boolean(
      'google' in window &&
      (window as Window & { google?: { maps?: unknown } }).google?.maps,
    );
  }

  private async renderGoogleMap(
    networkMap: HTMLElement,
    points: EcomCashoutMapPoint[],
  ): Promise<void> {
    const { Map: googleMap, InfoWindow: infoWindowConstructor } =
      (await google.maps.importLibrary(
        'maps',
      )) as google.maps.MapsLibrary;
    const { AdvancedMarkerElement: advancedMarkerElement } =
      (await google.maps.importLibrary(
        'marker',
      )) as google.maps.MarkerLibrary;
    const placeAutocompleteElement = (
      (await google.maps.importLibrary(
        'places',
      )) as google.maps.PlacesLibrary
    ).PlaceAutocompleteElement as PlaceAutocompleteElementConstructor;

    this.map = new googleMap(networkMap, {
      center: DEFAULT_MAP_POSITION,
      zoom: DEFAULT_MAP_ZOOM,
      mapId: GOOGLE_MAPS_MAP_ID,
    });

    this.setupSearch(placeAutocompleteElement);

    const infoWindow = new infoWindowConstructor({ maxWidth: 250 });
    const markers = points.map((point) => {
      const marker = new advancedMarkerElement({
        map: this.map,
        position: point.position,
        title: point.salepointName,
        content: this.createMarkerContent(point.iconName),
        gmpClickable: true,
      });

      marker.addEventListener('gmp-click', () =>
        this.openPointInfoWindow(infoWindow, marker, point),
      );

      return marker;
    });

    this.markerClusterer?.clearMarkers();
    this.markerClusterer = new MarkerClusterer({
      map: this.map,
      markers,
    });

    this.setCurrentPositionMarker(advancedMarkerElement, markers);
  }

  private openPointInfoWindow(
    infoWindow: google.maps.InfoWindow,
    marker: google.maps.marker.AdvancedMarkerElement,
    point: EcomCashoutMapPoint,
  ): void {
    infoWindow.setContent(this.createInfoWindowContent(point));
    infoWindow.open({
      anchor: marker,
      map: this.map,
    });
  }

  private setupSearch(
    placeAutocompleteElement: PlaceAutocompleteElementConstructor,
  ): void {
    if (!this.map) {
      return;
    }

    const autocomplete = new placeAutocompleteElement({});
    autocomplete.id = 'pac-input';
    autocomplete.placeholder = this.translateService.instant(
      'ECOMCASHOUT.SETECOM.SEARCHGOOGLEMAP',
    ) as string;

    this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(autocomplete);

    autocomplete.addEventListener('gmp-select', async ({ placePrediction }) => {
      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: ['location', 'viewport'],
      });

      if (!place.location || !this.map) {
        return;
      }

      if (place.viewport) {
        this.map.fitBounds(place.viewport);
      } else {
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(place.location);
        this.map.fitBounds(bounds);
      }

      this.map.setZoom(
        Math.min(this.map.getZoom() ?? DEFAULT_MAP_ZOOM, MAX_SEARCH_ZOOM),
      );
    });
  }

  private setCurrentPositionMarker(
    advancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement,
    markers: google.maps.marker.AdvancedMarkerElement[],
  ): void {
    if (!navigator.geolocation || !this.map) {
      return;
    }

    navigator.geolocation.getCurrentPosition((position) => {
      if (!this.map) {
        return;
      }

      const currentPosition = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };

      const marker = new advancedMarkerElement({
        map: this.map,
        position: currentPosition,
        content: this.createMarkerContent('Position'),
      });

      markers.push(marker);
      this.markerClusterer?.addMarker(marker);
    });
  }

  private createMarkerContent(iconName: string): HTMLElement {
    const image = this.document.createElement('img');
    const isPosition = iconName === 'Position';

    image.src = isPosition
      ? '/content/images/map_position_paynet.png'
      : '/content/images/icon-map-paynet.png';
    image.alt = '';

    return image;
  }

  private createInfoWindowContent(point: EcomCashoutMapPoint): HTMLElement {
    const content = this.document.createElement('div');
    const title = this.document.createElement('h4');
    const description = this.document.createElement('p');

    title.textContent = point.salepointName;
    title.style.fontSize = '20px';
    title.style.color = '#2d3947';
    title.style.margin = '0px';

    this.appendDescriptionLines(description, point.salepointDescription);
    description.style.color = '#2d3947';
    description.style.fontSize = '14px';
    description.style.fontWeight = '400';

    content.append(title, description);

    return content;
  }

  private appendDescriptionLines(element: HTMLElement, value: string): void {
    value
      .split(/<br\s*\/?>/i)
      .filter((line) => line.trim().length > 0)
      .forEach((line, index) => {
        if (index > 0) {
          element.append(this.document.createElement('br'));
        }

        element.append(this.document.createTextNode(line));
      });
  }

  private appendExternalScript(src: string): Promise<void> {
    const existingScript = Array.from(this.document.scripts).find(
      (script) => script.src === new URL(src, this.document.baseURI).href,
    );

    if (existingScript) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const script = this.document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Script failed: ${src}`));
      this.document.body.appendChild(script);
    });
  }
}
